// SendBackDialog (job page): the office sends a job waiting for review back to the tech, on a FormDialog:
// why (one choice) and what to tell them (required). The tech sees the message on their phone with the
// job, and the job goes back to under way until they send it in again.
import { useState } from 'react';
import { Choice, ChoiceGroup } from '../components/ChoiceGroup/ChoiceGroup.tsx';
import { FormDialog } from '../components/Dialog/Dialog.tsx';
import { TextField } from '../components/Field/Field.tsx';

export const SEND_BACK_REASONS = [
  { value: 'photos', label: 'Photos missing' },
  { value: 'form', label: 'A form isn’t filled in' },
  { value: 'parts', label: 'Parts don’t match' },
  { value: 'other', label: 'Something else' },
] as const;
export type SendBackReason = (typeof SEND_BACK_REASONS)[number]['value'];

export interface SendBackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** The tech's first name ("Sam"). */
  tech: string;
  /** What it starts with: the fixes the review found, if any. */
  reason?: SendBackReason;
  message?: string;
  onSend: (reason: SendBackReason, message: string) => Promise<void> | void;
  previewState?: 'failed' | 'discard';
}

export function SendBackDialog({
  isOpen,
  onClose,
  tech,
  reason: startReason = 'photos',
  message: startMessage = '',
  onSend,
  previewState,
}: SendBackDialogProps) {
  const [reason, setReason] = useState<SendBackReason>(startReason);
  const [message, setMessage] = useState(startMessage);
  const [tried, setTried] = useState(false);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setReason(startReason);
      setMessage(startMessage);
      setTried(false);
    }
  }
  const missing = message.trim() ? null : `Say what to fix, so ${tech} knows what to do.`;
  return (
    <FormDialog
      title={`Send back to ${tech}`}
      submitLabel={`Send back to ${tech}`}
      isOpen={isOpen}
      onClose={onClose}
      isDirty={reason !== startReason || message !== startMessage}
      previewState={previewState}
      failText={`It didn’t send. ${tech} hasn’t been told; your message is still here.`}
      validate={() => {
        setTried(true);
        return !missing;
      }}
      onSubmit={async () => {
        await onSend(reason, message.trim());
      }}
    >
      <ChoiceGroup
        label="Why"
        selectionMode="single"
        value={reason}
        onChange={(r) => setReason(r as SendBackReason)}
      >
        {SEND_BACK_REASONS.map((r) => (
          <Choice key={r.value} value={r.value}>
            {r.label}
          </Choice>
        ))}
      </ChoiceGroup>
      <TextField
        label={`What to tell ${tech}`}
        multiline
        isRequired
        autoFocus
        value={message}
        onChange={setMessage}
        placeholder="e.g. There’s no photo of the flex line. Add one from the job on your phone."
        description={`${tech} sees this on their phone with the job. It goes back to under way until they send it in again.`}
        error={tried ? missing : null}
      />
    </FormDialog>
  );
}
